import React, { useEffect } from 'react';
import { devLog } from '../../utils/devLog';
import { useCustomDialogs } from '../../hooks/useCustomDialogs';

const StorageManager = () => {
  const { showConfirm, showAlert } = useCustomDialogs();

  useEffect(() => {
    // Check storage on mount
    checkStorageQuota();
    
    // Auto-cleanup if storage is nearly full
    autoCleanupIfNeeded();
    
    // Add global function to clear storage
    window.clearAppStorage = clearStorage;
    window.checkStorageInfo = getStorageInfo;
    window.autoCleanupStorage = autoCleanupIfNeeded;
  }, []);

  const checkStorageQuota = () => {
    try {
      // Try to estimate storage quota
      if (navigator.storage && navigator.storage.estimate) {
        navigator.storage.estimate().then(async estimate => {
          const percentUsed = (estimate.usage / estimate.quota) * 100;
          if (percentUsed > 90) {

            // Show user notification if we have the dialog hook available
            if (typeof showAlert === 'function') {
              showAlert(
                `Storage is critically full (${percentUsed.toFixed(0)}% used). ` +
                `The app will automatically clean up old images to free space. ` +
                `You can also visit /clear-storage.html for manual cleanup.`
              );
            }
            
            // Auto-cleanup immediately
            await autoCleanupIfNeeded();
          } else if (percentUsed > 70) {

          }
        });
      }
    } catch (e) {

    }
  };

  const clearStorage = () => {
    const confirm = window.confirm(
      '⚠️ This will clear image data and temporary files.\n\n' +
      'Your crew, drills, and other records will be preserved.\n\n' +
      'Continue?'
    );
    
    if (!confirm) return;

    let clearedCount = 0;
    const keysToRemove = [];
    
    // Clear images and large data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('image') || 
        key.includes('photo') || 
        key.includes('picture') ||
        key.includes('temp_') ||
        key.includes('cache_') ||
        key.includes('_backup')
      )) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        clearedCount++;
      } catch (e) {

      }
    });
    
    devLog(`✅ Cleared ${clearedCount} items from storage`);
    alert(`Storage cleaned! Removed ${clearedCount} temporary items.`);
    
    // Refresh to apply changes
    window.location.reload();
  };

  const getStorageInfo = () => {
    let totalSize = 0;
    const items = {};
    
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        try {
          const value = localStorage.getItem(key);
          const size = new Blob([value]).size;
          totalSize += size;
          items[key] = size;
        } catch (e) {

        }
      }
    }
    
    const sortedItems = Object.entries(items)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
    
    devLog('=== Storage Info ===');
    devLog(`Total Size: ${(totalSize / (1024 * 1024)).toFixed(2)} MB`);
    devLog(`Total Items: ${Object.keys(items).length}`);
    devLog('\nTop 10 Largest Items:');
    sortedItems.forEach(([key, size]) => {
      devLog(`  ${key}: ${(size / 1024).toFixed(1)} KB`);
    });
    
    return { totalSize, items: sortedItems };
  };

  const autoCleanupIfNeeded = async () => {
    try {
      // Check if we can estimate storage
      if (!navigator.storage || !navigator.storage.estimate) {
        return;
      }

      const estimate = await navigator.storage.estimate();
      const percentUsed = (estimate.usage / estimate.quota) * 100;
      
      // If storage is over 80% full, auto-cleanup images
      if (percentUsed > 80) {

        devLog('Auto-cleaning equipment images...');
        
        const keysToRemove = [];
        
        // Find all equipment images
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('equipment_image_')) {
            keysToRemove.push(key);
          }
        }
        
        // Remove equipment images
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
            devLog(`Removed: ${key}`);
          } catch (e) {

          }
        });
        
        if (keysToRemove.length > 0) {
          devLog(`✅ Auto-cleaned ${keysToRemove.length} equipment images`);
        }
      }
    } catch (e) {

    }
  };

  // Don't render anything
  return null;
};

export default StorageManager;